import { useState, useCallback, useRef, useEffect } from 'react';
import { InlineAssistantOptions, InlineAssistantResult } from './types';
import { getInlineAssistantFactory, InlineAssistant, ensureInlineAssistantInitialized } from './inlineAssistant';

/**
 * React hook for inline assistant generation with streaming support.
 *
 * This hook enables generating content (like panel titles or descriptions) inline
 * without opening the assistant sidebar. Each hook instance creates its own chat
 * conversation, so all generations from the same instance are grouped together.
 * Generated content is ephemeral - it won't appear in the user's chat history
 * but will be tracked in the admin UI for analytics.
 *
 * Uses the full BaseMultiStepAgent infrastructure for tool execution and multi-step workflows.
 *
 * The implementation is lazy-loaded on first use to reduce initial bundle size.
 *
 * @example
 * ```typescript
 * function PanelEditor() {
 *   const { generate, isGenerating, content, error, reset } = useInlineAssistant();
 *   const [title, setTitle] = useState('');
 *
 *   const handleGenerate = () => {
 *     generate({
 *       prompt: 'Generate a panel title for CPU metrics',
 *       origin: 'grafana/panel-editor/title',
 *       systemPrompt: 'Generate only the title text, no explanation',
 *       onComplete: (text) => setTitle(text.trim()),
 *       onError: (err) => console.error(err)
 *     });
 *   };
 *
 *   return (
 *     <div>
 *       <input value={title} onChange={(e) => setTitle(e.target.value)} />
 *       <button onClick={handleGenerate} disabled={isGenerating}>
 *         {isGenerating ? 'Generating...' : 'Generate with Assistant'}
 *       </button>
 *       {isGenerating && <div>{content}</div>}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Using tools with inline assistant
 * import { lokiQueryHandlerTool } from 'agent/tools';
 *
 * function ComponentWithTools() {
 *   const { generate } = useInlineAssistant();
 *
 *   const handleGenerate = async () => {
 *     await generate({
 *       prompt: 'Query Loki for error logs from the last hour',
 *       origin: 'my-app/component',
 *       tools: [lokiQueryHandlerTool],
 *       onComplete: (text) => console.log('Generated:', text),
 *     });
 *   };
 *
 *   return <button onClick={handleGenerate}>Generate</button>;
 * }
 * ```
 */
export function useInlineAssistant(): InlineAssistantResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [content, setContent] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const assistantRef = useRef<InlineAssistant | null>(null);

  /**
   * Reset error and content state
   */
  const reset = useCallback(() => {
    setError(null);
    setContent('');
  }, []);

  /**
   * Cancel the current generation
   */
  const cancel = useCallback(() => {
    if (assistantRef.current) {
      assistantRef.current.cancel();
    }
    setIsGenerating(false);
  }, []);

  /**
   * Generate content with the given options
   */
  const generate = useCallback(async (options: InlineAssistantOptions): Promise<void> => {
    // Reset state
    setError(null);
    setContent('');
    setIsGenerating(true);

    try {
      // Ensure the inline assistant is initialized (lazy-loads on first use)
      await ensureInlineAssistantInitialized();

      const factory = getInlineAssistantFactory();

      // Create or reuse assistant for this origin
      if (!assistantRef.current) {
        assistantRef.current = await factory(options.origin);
      }

      await assistantRef.current.sendPrompt({
        prompt: options.prompt,
        agentName: options.agentName,
        agentId: options.agentId,
        systemPrompt: options.systemPrompt,
        tools: options.tools,
        onStart: () => {
          setIsGenerating(true);
        },
        onDelta: (delta) => {
          setContent((prev) => prev + delta);
          options.onDelta?.(delta);
        },
        onComplete: (text) => {
          setContent(text);
          setIsGenerating(false);
          options.onComplete?.(text);
        },
        onError: (err) => {
          setError(err);
          setIsGenerating(false);
          options.onError?.(err);
        },
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setIsGenerating(false);
      options.onError?.(error);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (assistantRef.current) {
        assistantRef.current.cancel();
        assistantRef.current.dispose();
        assistantRef.current = null;
      }
    };
  }, []);

  return {
    generate,
    isGenerating,
    content,
    error,
    cancel,
    reset,
  };
}
